// ** React Imports
import { createContext, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { consumePostAuthRedirect } from 'src/utils/guestQms'

// ** Config
import authConfig from '../configs/auth'
import FallbackSpinner from 'src/@core/components/spinner'
import { ApiEndPoints } from 'src/network/endpoints'
import { axiosInstance } from 'src/network/adapter'

// ** Defaults
const defaultProvider = {
  user: null,
  loading: true,
  fcmToken: '',
  setUser: () => null,
  setFcmToken: () => null,
  setLoading: () => Boolean,
  isInitialized: false,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  setIsInitialized: () => Boolean,
  register: () => Promise.resolve(),
  refreshUser: () => Promise.resolve(null),
  rolePermissions: [],
  setRolePermission: () => []
}
const AuthContext = createContext(defaultProvider)

const AuthProvider = ({ children }) => {
  // ** States
  const [user, setUser] = useState(defaultProvider.user)
  const [loading, setLoading] = useState(defaultProvider.loading)
  const [isInitialized, setIsInitialized] = useState(defaultProvider.isInitialized)
  const [fcmToken, setFcmToken] = useState(defaultProvider.fcmToken)
  const [rolePermissions, setRolePermission] = useState(defaultProvider.rolePermissions)

  const navigate = useNavigate()
  // const [unReadNotificationCount, setUnReadNotificationCount] = useState(defaultProvider.unReadNotificationCount)

  // ** Hooks
  const [searchParams] = useSearchParams()

  // const getUnReadNotificationCount = () => {
  //   axiosInstance
  //     .get(ApiEndPoints.NOTIFICATIONS.get_unread_notifications_count)
  //     .then(response => response.data)
  //     .then(response => {
  //       // console.log('getUnReadNotificationCount', response)
  //       setUnReadNotificationCount(response?.count)
  //     })
  //     .catch(error => {
  //       console.log('error', error)
  //     })
  // }

  useEffect(() => {
    const initAuth = async () => {
      setIsInitialized(true)
      const storedToken = window.localStorage.getItem(authConfig.storageTokenKeyName)

      if (storedToken) {
        setLoading(true)
        try {
          const response = await axios.get(ApiEndPoints.AUTH.me, {
            headers: { Authorization: `Bearer ${storedToken}` }
          })

          const userData = response?.data?.data?.user
          setUser({ ...userData })

          // ✅ Set role permissions if available
          if (userData?.roleId?.permissions) {
            setRolePermission(userData.roleId.permissions)
          }

          window.localStorage.setItem(authConfig.storageUserDataKeyName, JSON.stringify(userData))
        } catch (error) {
          localStorage.removeItem(authConfig.storageUserDataKeyName)
          localStorage.removeItem(authConfig.storageTokenKeyName)
          setUser(null)
        } finally {
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    }

    initAuth()
  }, [])

  const persistUserSession = (token, user) => {
    window.localStorage.setItem(authConfig.storageTokenKeyName, token)
    window.localStorage.setItem(authConfig.storageUserDataKeyName, JSON.stringify(user))
    setUser(user)

    if (user?.roleId?.permissions) {
      setRolePermission(user.roleId.permissions)
    }
  }

  const refreshUser = async () => {
    const storedToken = window.localStorage.getItem(authConfig.storageTokenKeyName)
    if (!storedToken) return null

    try {
      const response = await axios.get(ApiEndPoints.AUTH.me, {
        headers: { Authorization: `Bearer ${storedToken}` }
      })

      const userData = response?.data?.data?.user
      if (userData) {
        window.localStorage.setItem(authConfig.storageUserDataKeyName, JSON.stringify(userData))
        setUser({ ...userData })

        if (userData?.roleId?.permissions) {
          setRolePermission(userData.roleId.permissions)
        }
      }

      return userData || null
    } catch {
      return null
    }
  }

  const handleLogin = ({ token, user }) => {
    persistUserSession(token, user)

    const redirectUrl = searchParams.get('redirect') || consumePostAuthRedirect()
    // Only allow same-origin relative paths to prevent open redirects
    const isSafeRedirect =
      typeof redirectUrl === 'string' &&
      redirectUrl.startsWith('/') &&
      !redirectUrl.startsWith('//') &&
      !redirectUrl.split('/', 1)[0].includes(':')
    navigate(isSafeRedirect ? redirectUrl : '/')
  }

  const handleLogout = async () => {
    try {
      await axiosInstance.post(ApiEndPoints.AUTH.logout)
    } catch (error) {
      // ignore logout API errors
    } finally {
      setUser(null)
      setRolePermission([])
      window.localStorage.removeItem(authConfig.storageUserDataKeyName)
      window.localStorage.removeItem(authConfig.storageTokenKeyName)
      navigate('/login')
    }
  }

  const handleRegister = ({ token, user }) => {
    persistUserSession(token, user)
  }

  const values = {
    fcmToken,
    setFcmToken,
    user,
    loading,
    setUser,
    setLoading,
    isInitialized,
    setIsInitialized,
    login: handleLogin,
    logout: handleLogout,
    register: handleRegister,
    refreshUser,
    rolePermissions,
    setRolePermission
  }

  return <AuthContext.Provider value={values}>{loading ? <FallbackSpinner /> : children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider }
